/**
 * One row of starters under its heading: the walkthrough first, until the
 * record is complete, then the five templates. Cards keep the production
 * home grid's count and minimum, three to two as the column narrows, and
 * page rather than shrink when they overflow; the arrows sit in the heading
 * row.
 * A column that holds one card stacks them all instead and lets the page
 * scroll, the way a workspace page does at that width. A starter that
 * already has a canvas with a preview is that canvas's card.
 */

import { getCanvasHeroImageUrl, type Canvas } from "@cohesive/api-client";
import { Button, useThemeContext } from "@cohesive/ui-components";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@cohesive/ui-components/ui/carousel";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { ChevronLeft, ChevronRight, Compass, Frame, Image } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { bundledPoster, POSTER_SIZES, posterUrl } from "./poster";
import {
  templateCopy,
  TOUR_CANVAS_DESCRIPTION,
  type HomeRecord,
} from "./record";
import { StarterCard } from "./StarterCard";
import { TEMPLATES, type Template } from "./starters";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";
import { useStarterCanvases } from "./useStarterCanvases";
import type { TemplateOpen } from "./useTemplateCopy";

/** The production home grid's card track and gap (`HOME_CARD_GRID_TRACK_PX`). */
export const CARD_TRACK_PX = 270;
export const CARD_GAP_PX = 16;
const MAX_CARDS = 3;

export const ROW_TITLE = "Start exploring";
export const WALKTHROUGH_TITLE = "Finish onboarding";
export const WALKTHROUGH_TAKE = "Take the guided tour of the canvas";
export const WALKTHROUGH_RESUME = TOUR_CANVAS_DESCRIPTION;

/** How many production-size cards the column holds, as auto-fill would. */
export function cardsThatFit(width: number): number {
  const fit = Math.floor((width + CARD_GAP_PX) / (CARD_TRACK_PX + CARD_GAP_PX));
  return Math.min(MAX_CARDS, Math.max(1, fit));
}

export function useCardCount(): [
  React.RefObject<HTMLDivElement | null>,
  number,
] {
  const ref = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(3);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Hidden behind the loader the column is 0 wide; keep the last count.
    const measure = () => {
      if (el.clientWidth > 0) setCount(cardsThatFit(el.clientWidth));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, count];
}

interface PosterCandidate {
  src: string;
  srcSet?: string;
}

/** Draws the first candidate that loads, the glyph when none does. */
function Poster({ candidates }: { candidates: readonly PosterCandidate[] }) {
  const [failed, setFailed] = useState<string[]>([]);
  const shown = candidates.find((c) => !failed.includes(c.src));
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-surface-card">
      {shown ? (
        <img
          key={shown.src}
          src={shown.src}
          srcSet={shown.srcSet}
          sizes={shown.srcSet ? POSTER_SIZES : undefined}
          alt=""
          draggable={false}
          loading="lazy"
          className="size-full object-cover"
          onError={() => setFailed((urls) => [...urls, shown.src])}
        />
      ) : (
        <Image
          className="size-8 text-muted-foreground"
          strokeWidth={1.75}
          aria-hidden
        />
      )}
    </div>
  );
}

/** The workspace home's heading over its list, with room for the arrows. */
function Heading({ children }: { children?: React.ReactNode }) {
  return (
    <div className="flex min-h-8 items-center justify-between gap-3">
      <h2 className="text-lg font-medium text-n-90">{ROW_TITLE}</h2>
      {children}
    </div>
  );
}

/** The onboarding starter reads as onboarding, not as one more canvas. */
function WalkthroughPreview() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#F5F0E5] to-[#EBE3D1] dark:from-[#221f19] dark:to-[#19160f]">
      <Compass
        className="size-10 text-[#B86E49]"
        strokeWidth={1.25}
        aria-hidden
      />
    </div>
  );
}

export interface HomeRowProps {
  record: HomeRecord;
  templates: TemplateOpen;
  /** Opens the walkthrough; the page shows its loader meanwhile. */
  onWalkthrough: () => void;
  /** Cards the column holds at production size; one stacks them. */
  cardCount: number;
}

export function HomeRow({
  record,
  templates,
  onWalkthrough,
  cardCount,
}: HomeRowProps) {
  const { walkthrough, stage } = record;
  const reducedMotion = usePrefersReducedMotion();
  const { mode } = useThemeContext();
  const canvases = useStarterCanvases([
    ...(walkthrough.canvasId ? [walkthrough.canvasId] : []),
    ...TEMPLATES.flatMap((template) => {
      const copy = templateCopy(record.record, template.id);
      return copy ? [copy] : [];
    }),
  ]);

  const [api, setApi] = useState<CarouselApi>();
  // One instance: a new plugins array re-inits embla, which resets the page.
  const [plugins] = useState(() => [WheelGesturesPlugin()]);
  const [selected, setSelected] = useState(0);
  const [snapCount, setSnapCount] = useState(0);
  const update = useCallback(() => {
    if (!api) return;
    setSelected(api.selectedScrollSnap());
    setSnapCount(api.scrollSnapList().length);
  }, [api]);
  useEffect(() => {
    if (!api) return;
    update();
    api.on("select", update).on("reInit", update);
    return () => {
      api.off("select", update).off("reInit", update);
    };
  }, [api, update]);

  if (!record.resolved) return null;

  const slides: Array<Template | "walkthrough"> = [
    ...(walkthrough.offered ? (["walkthrough"] as const) : []),
    ...TEMPLATES,
  ];
  // The canvas behind a starter, and its preview once the capture exists.
  const real = (id: string | undefined): [Canvas?, string?] => {
    const canvas = id ? canvases[id] : undefined;
    return [canvas, canvas && getCanvasHeroImageUrl(canvas, mode)];
  };
  const card = (slide: Template | "walkthrough") => {
    if (slide === "walkthrough") {
      const [canvas, hero] = real(walkthrough.canvasId);
      return (
        <StarterCard
          title={WALKTHROUGH_TITLE}
          subtitle={
            canvas?.description ||
            (stage === "started" ? WALKTHROUGH_RESUME : WALKTHROUGH_TAKE)
          }
          icon={hero ? Frame : Compass}
          preview={
            hero ? (
              <Poster candidates={[{ src: hero }]} />
            ) : (
              <WalkthroughPreview />
            )
          }
          onOpen={onWalkthrough}
          busy={walkthrough.pending}
        />
      );
    }
    const [canvas, hero] = real(templateCopy(record.record, slide.id));
    const bundled = bundledPoster(slide.id, mode);
    return (
      <StarterCard
        title={slide.title}
        subtitle={canvas?.description || slide.tagline}
        preview={
          <Poster
            candidates={
              hero
                ? [{ src: hero }]
                : [...(bundled ? [bundled] : []), { src: posterUrl(slide.id) }]
            }
          />
        }
        onOpen={() => templates.open(slide)}
        busy={templates.pending === slide.id}
      />
    );
  };

  if (cardCount === 1) {
    return (
      <div
        data-testid="home-row"
        data-stacked=""
        aria-label="Starters"
        className="flex flex-col gap-4"
      >
        <Heading />
        {slides.map((slide) => (
          <div key={slide === "walkthrough" ? slide : slide.id}>
            {card(slide)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Heading>
        {snapCount > 1 && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="Show previous starters"
              disabled={selected === 0}
              onClick={() => api?.scrollPrev()}
            >
              <ChevronLeft aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="Show next starters"
              disabled={selected >= snapCount - 1}
              onClick={() => api?.scrollNext()}
            >
              <ChevronRight aria-hidden />
            </Button>
          </div>
        )}
      </Heading>
      <Carousel
        aria-label="Starters"
        data-testid="home-row"
        setApi={setApi}
        opts={{
          align: "start",
          containScroll: "trimSnaps",
          slidesToScroll: "auto",
          duration: reducedMotion ? 0 : 22,
        }}
        plugins={plugins}
      >
        <CarouselContent className="py-1 touch-pan-y touch-pinch-zoom select-none">
          {slides.map((slide, index) => (
            <CarouselItem
              key={slide === "walkthrough" ? slide : slide.id}
              style={{ flexBasis: `${100 / cardCount}%` }}
              aria-label={`${index + 1} of ${slides.length}`}
            >
              {card(slide)}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
